'use client'

import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cn } from '../../lib/utils'
import { VariantProps, cva } from 'class-variance-authority'

const tabsListVariants = cva('', {
  variants: {
    variant: {
      filled:
        'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
      underline:
        'inline-flex items-center justify-center whitespace-nowrap py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background relative rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none',
      border:
        'inline-flex h-10  items-center justify-center rounded-md p-1 text-muted-foreground border border-primary',
    },
  },
  defaultVariants: {
    variant: 'border',
  },
})

interface TabsListContext extends VariantProps<typeof tabsListVariants> {}

const TabsListContext = React.createContext<TabsListContext>({
  variant: 'border',
})

const TabsListProvider = (props: React.PropsWithChildren<TabsListContext>) => {
  return (
    <TabsListContext.Provider value={props}>
      {props.children}
    </TabsListContext.Provider>
  )
}

const useTabsListContext = () => React.useContext(TabsListContext)

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Tabs>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Tabs> & {
    variant?: TabsListContext['variant']
  }
>(({ variant = 'border', ...props }, ref) => {
  return (
    <TabsListProvider variant={variant}>
      <TabsPrimitive.Tabs {...props} ref={ref} />
    </TabsListProvider>
  )
})

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const { variant } = useTabsListContext()

  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'space-x-5',
        tabsListVariants({
          className,
          variant,
        }),
        {
          'space-x-12': variant === 'underline',
        },
        className
      )}
      {...props}
    />
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { variant } = useTabsListContext()

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1 text-sm  font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm',
        {
          'data-[state=active]:bg-primary data-[state=active]:text-white text-primary':
            variant === 'border',
          'data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none border-b-[2px] border-b-transparent rounded-none px-0':
            variant === 'underline',
        },
        className
      )}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
